import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { constants } from '../../constants';
import { mediaQuery, useMediaQuery } from '../../hooks/useMediaQuery';
import useMyMApi from '../../hooks/useMyMApi';
import useWebContext from '../../hooks/useWebContext';
import { BreadcrumbItemContext } from '../../provider/BreadcrumbItemContextProvider';
import { MyMImage } from '../../ui-elements/MyMImage';
import { MyMLabel, MyMVariantLabel } from '../../ui-elements/MyMLabel';
import { MyMModal } from '../../ui-elements/MyMModal';
import { MyMProgress } from '../../ui-elements/MyMProgress';
import { MetadataContentTabs } from '../../ui-parts/MetadataContentTabs';
import Preview, { previewModalSize } from '../MyM906_Preview/Preview';

/**
 * SalesLv2 Page Component
 */
const SalesLv2 = () => {

  const screenId = 'MyM302';
  const { controlSettings } = useWebContext(screenId);
  const apiRequest = useMyMApi(screenId);
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState(null);
  const navigate = useNavigate();
  const queryParam = useParams();
  const { setBreadcrumbItem } = useContext(BreadcrumbItemContext);
  const isPc = useMediaQuery(mediaQuery.pc);
  // 2025.3.5 MyMine Version2.1対応 Add Start
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewFileId, setPreviewFileId] = useState(false);
  const handleModalClose = () => setIsModalOpen(false);
  const location = useLocation();
  // 2025.3.5 MyMine Version2.1対応 Add End
  useEffect(() => {
    /**
     * Contents情報取得成功時処理
     * @param {any} result
     */
    const callbackSuccessRequestSalesContents = (result) => {

      if (result === null) {
        // 取得情報がない場合、失敗処理を行う
        callbackFailedRequestSalesContents();
        return;
      }
      // パンくずリストにMetadataNameを設定
      setBreadcrumbItem(result.ModelMetadataName ?? result.MetadataCategoryName);
      setContent(result);
      setIsLoading(false);

      // 2025.3.5 MyMine Version2.1対応 Add Start
      if (location.state?.previewFileId) {
        setPreviewFileId(location.state.previewFileId);
        setIsModalOpen(true);
      }
      // 2025.3.5 MyMine Version2.1対応 Add End
    }

    /**
     * Contents情報取得失敗時処理
     */
    const callbackFailedRequestSalesContents = () => {
      setIsLoading(false);
      // エラーページ遷移
      navigate('/MyM999', { state: { message: constants.Message.InvalidRequest } });
    }

    // 表示情報取得
    apiRequest.requestJson(`api/sales/lv2?metadataId=${queryParam.metadata_id}`, 'GET', callbackSuccessRequestSalesContents, callbackFailedRequestSalesContents);

    // アンマウント時、パンくずリストのコンテキストをクリア
    return () => {
      setBreadcrumbItem(null);
    }
  }, []);

  if (content == null)
    return <MyMProgress />

  const salesLv2Label =
    controlSettings.LabelList['lblSalesLv2Massage_' + content.MetadataCategoryId] ??
    controlSettings.LabelList['lblSalesLv2Massage_' + queryParam.metadata_id];

  const salesLv2Image =
    controlSettings.ImageList['imgSalesLv2_' + content.MetadataCategoryId] ?
      isPc ?
        controlSettings.ImageList['imgSalesLv2_' + content.MetadataCategoryId] :
        controlSettings.ImageList['imgSalesLv2_' + content.MetadataCategoryId + '_mobile']
      :
      isPc ?
        controlSettings.ImageList['imgSalesLv2_' + queryParam.metadata_id] :
        controlSettings.ImageList['imgSalesLv2_' + queryParam.metadata_id + '_mobile'];

  // 2025.3.5 MyMine Version2.1対応 Add Start
  // パラメータのメタデータ
  const isBusinessAreaMetadata = content.MetadataCategoryId != null;
  // 2025.3.5 MyMine Version2.1対応 Add End

  return (
    <Box className='py-1 d-flex flex-column'>
      {isLoading && <MyMProgress />}
      <Box className='mb-4'>
        <MyMVariantLabel variant='h5'>{content.ModelMetadataName ?? content.MetadataCategoryName}</MyMVariantLabel>
        <MyMLabel>{salesLv2Label}</MyMLabel>
      </Box>
      {isPc ?
        <>
          <Grid container className='pb-2 flex-grow-1'>
            <Grid item xs={12} md={3} className='d-flex justify-content-center' sx={{ '> img': { maxHeight: 550 } }}>
              {salesLv2Image && <MyMImage screenId={screenId} filePath={salesLv2Image} />}
            </Grid>
            <Grid item xs={12} md={8.7}>
              <MetadataContentTabs
                paramMetadataId={queryParam.metadata_id}
                screenId={screenId}
                metadataContent={content}
                setIsLoading={setIsLoading}
                // 2025.3.5 MyMine Version2.1対応 Add Start
                canShareContents={true}
                contentsShareParameter={location.state}
                // Modelからの遷移の場合、先頭アコーディオンを開いた状態にする
                isDefaultOpenFirstAccordion={!isBusinessAreaMetadata}
                // 2025.3.5 MyMine Version2.1対応 Add End
              />
            </Grid>
          </Grid>
        </>
        :
        <>
          <Grid container className='pb-2 flex-grow-1'>
            <Grid item xs={12} md={3} className='d-flex justify-content-center' sx={{ '> img': { maxWidth: '100%', maxHeight: 550 } }}>
              {salesLv2Image && <MyMImage screenId={screenId} filePath={salesLv2Image} />}
            </Grid>
            <Grid item xs={12} md={8.7}>
              <MetadataContentTabs
                paramMetadataId={queryParam.metadata_id}
                screenId={screenId}
                metadataContent={content}
                setIsLoading={setIsLoading}
                // 2025.3.5 MyMine Version2.1対応 Add Start
                canShareContents={true}
                contentsShareParameter={location.state}
                // Modelからの遷移の場合、先頭アコーディオンを開いた状態にする
                isDefaultOpenFirstAccordion={!isBusinessAreaMetadata}
                // 2025.3.5 MyMine Version2.1対応 Add End
              />
            </Grid>
          </Grid>
        </>
      }
      {/* 2025.3.5 MyMine Version2.1対応 Add Start */}
      <MyMModal
        open={isModalOpen}
        onClose={handleModalClose}
        {...previewModalSize}
      >
        <Preview fileId={previewFileId} onPreviewFailed={handleModalClose} />
      </MyMModal>
      {/* 2025.3.5 MyMine Version2.1対応 Add End */}
    </Box>

  );
}
export default SalesLv2;
